import React, { useState } from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';

interface PasswordInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  showStrength?: boolean;
  validateHistory?: boolean;
  passwordHistory?: string[];
}

interface PasswordValidation {
  hasMinLength: boolean;
  hasNumber: boolean;
  hasSymbol: boolean;
  notInHistory: boolean;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  label = 'Senha',
  value,
  onChange,
  required = false,
  showStrength = true,
  validateHistory = false,
  passwordHistory = []
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [validation, setValidation] = useState<PasswordValidation>({
    hasMinLength: false,
    hasNumber: false,
    hasSymbol: false,
    notInHistory: true
  });

  const validatePassword = (password: string): PasswordValidation => {
    const hasMinLength = password.length >= 8;
    const hasNumber = /\d/.test(password);
    const hasSymbol = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    
    // Verificar histórico de senhas (últimas 3)
    let notInHistory = true;
    if (validateHistory && passwordHistory.length > 0) {
      // Aqui você deveria comparar com hashes, mas para simplicidade vamos comparar strings
      // Em produção, isso seria feito no backend
      notInHistory = !passwordHistory.slice(-3).includes(password);
    }

    return {
      hasMinLength,
      hasNumber,
      hasSymbol,
      notInHistory
    };
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    if (newValue) {
      const newValidation = validatePassword(newValue);
      setValidation(newValidation);
    } else {
      setValidation({
        hasMinLength: false,
        hasNumber: false,
        hasSymbol: false,
        notInHistory: true
      });
    }
  };

  const getStrengthColor = (): string => {
    const validCount = Object.values(validation).filter(Boolean).length;
    if (validCount === 4) return '#10b981'; // Verde
    if (validCount >= 2) return '#f59e0b'; // Amarelo
    return '#ef4444'; // Vermelho
  };

  const getStrengthText = (): string => {
    const validCount = Object.values(validation).filter(Boolean).length;
    if (validCount === 4) return 'Forte';
    if (validCount >= 2) return 'Média';
    return 'Fraca';
  };

  const isValid = validation.hasMinLength && validation.hasNumber && validation.hasSymbol && validation.notInHistory;

  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
        {label} {required && '*'}
      </label>
      
      <div style={{ position: 'relative' }}>
        <input
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={handleChange}
          required={required}
          style={{
            width: '100%',
            padding: '8px 80px 8px 36px',
            border: `1px solid ${value && !isValid ? '#ef4444' : '#ddd'}`,
            borderRadius: '6px',
            fontSize: '14px',
            outline: 'none',
            transition: 'border-color 0.2s'
          }}
          placeholder="Digite sua senha"
        />
        
        <div style={{
          position: 'absolute',
          left: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#6b7280'
        }}>
          <Lock size={18} />
        </div>
        
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '4px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>

      {value && showStrength && (
        <div style={{ marginTop: '8px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '4px'
          }}>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>
              Força da senha:
            </span>
            <span style={{
              fontSize: '12px',
              fontWeight: '600',
              color: getStrengthColor()
            }}>
              {getStrengthText()}
            </span>
          </div>
          
          <div style={{
            height: '4px',
            backgroundColor: '#e5e7eb',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${(Object.values(validation).filter(Boolean).length / 4) * 100}%`,
              backgroundColor: getStrengthColor(),
              transition: 'width 0.3s, background-color 0.3s'
            }} />
          </div>

          <div style={{ marginTop: '8px', fontSize: '12px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: validation.hasMinLength ? '#10b981' : '#6b7280',
              marginBottom: '2px'
            }}>
              <span>{validation.hasMinLength ? '✓' : '○'}</span>
              <span>Mínimo 8 caracteres</span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: validation.hasNumber ? '#10b981' : '#6b7280',
              marginBottom: '2px'
            }}>
              <span>{validation.hasNumber ? '✓' : '○'}</span>
              <span>Pelo menos um número</span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: validation.hasSymbol ? '#10b981' : '#6b7280',
              marginBottom: '2px'
            }}>
              <span>{validation.hasSymbol ? '✓' : '○'}</span>
              <span>Pelo menos um símbolo (!@#$%...)</span>
            </div>
            {validateHistory && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: validation.notInHistory ? '#10b981' : '#ef4444',
                marginBottom: '2px'
              }}>
                <span>{validation.notInHistory ? '✓' : '✗'}</span>
                <span>Não pode ser uma das últimas 3 senhas</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordInput;

